/**
 * Zustand store для управления subscription limits
 * Централизованное хранилище с кэшированием для предотвращения частых запросов
 */

import { create } from 'zustand';
import { useTeamStore } from './useTeamStore';
import { apiClient } from '../services/api';
import { hasAuthData } from '../utils/authHeaders';

export interface SubscriptionTier {
  tier: 'free' | 'basic' | 'pro' | 'max';
  status: 'active' | 'expired' | 'cancelled';
  expiresAt: string | null;
}

export interface ChatLimits {
  messagesPerDay: number; // -1 = unlimited
  webSearchPerDay: number;
  filesPerDay: number;
  deepResearchPerDay?: number; // Deep Research limit per day
  maxFilesPerMessage: number; // Maximum files in one message
  thinkingMode: boolean | string[]; // false or array of allowed levels
  voiceMessages: boolean;
  transcription: boolean;
  allowedModels: {
    chatgpt: string[];
    gemini: string[];
    deepseek: string[];
  };
  dailyModelLimits: {
    [model: string]: number; // -1 = unlimited
  };
}

export interface ImageLimits {
  imagesPerDay: number; // -1 = unlimited
  maxImagesPerGeneration: number;
  maxConcurrentGenerations: number;
  allowedProviders: {
    [provider: string]: {
      enabled: boolean;
      qualities?: string[];
      resolutions?: string[];
      inputImage?: boolean;
      models?: string[];
    };
  };
}

export interface VideoLimits {
  enabled: boolean;
  maxConcurrentGenerations?: number;
  allowedProviders: {
    [provider: string]: {
      enabled: boolean;
      durations?: number[];
      resolutions?: {
        [resolution: string]: number[];
      };
      quality?: string[];
      audioEnabled?: boolean;
    };
  };
}

export interface Limits {
  chat: ChatLimits;
  images: ImageLimits;
  video: VideoLimits;
  fileTypeRestrictions?: {
    [provider: string]: {
      images?: boolean;
      pdf?: boolean;
      documents?: boolean;
      code?: boolean;
      spreadsheets?: boolean;
      text?: boolean;
      video?: boolean;
      audio?: boolean;
    };
  };
}

export interface Usage {
  messages: number;
  webSearches: number;
  fileRequests: number;
  deepResearch?: number; // Deep Research usage counter
  voiceMessages: number;
  modelUsage: {
    [model: string]: number;
  };
  imagesGenerated: number;
  videosGenerated: number;
  activeImageGenerations?: number;
  activeVideoGenerations?: number;
  resetTime?: string | null; // Время сброса лимитов (ISO string) для free tier
}

// Маппинги моделей (единый источник истины с бэкенда)
export interface ChatAllowedModels {
  chatgpt: string[];
  gemini: string[];
  deepseek: string[];
  claude: string[];
}

export interface ModelMappings {
  video: Record<string, string>; // model ID -> limit key
  chat?: {
    allowedModels: Record<string, ChatAllowedModels>; // tier -> provider -> models
    providers: string[];
  };
  image?: {
    providers: string[];
  };
}

export interface SubscriptionLimitsData {
  subscription: SubscriptionTier;
  limits: Limits;
  usage: Usage;
  modelMappings?: ModelMappings; // Маппинги моделей с бэкенда
}

interface SubscriptionLimitsState {
  data: SubscriptionLimitsData | null;
  isLoading: boolean;
  error: string | null;
  fetchLimits: (forceRefresh?: boolean) => Promise<void>;
  incrementUsageOptimistic: (increments: {
    messages?: number;
    webSearches?: number;
    fileRequests?: number;
    images?: number;
  }) => void;
  rollbackUsageOptimistic: (increments: {
    messages?: number;
    webSearches?: number;
    fileRequests?: number;
    images?: number;
  }) => void;
}

// Кэш для предотвращения частых запросов
let lastFetchTime = 0;
let errorCount = 0; // Счётчик ошибок для exponential backoff
const CACHE_DURATION = 30000; // 30 секунд - уменьшено с 60 для более свежих данных
const ERROR_RETRY_BASE = 5000; // Базовая задержка при ошибке: 5 секунд
const MAX_ERROR_RETRY_DELAY = 120000; // Максимальная задержка: 2 минуты

// Очищаем кеш при загрузке модуля для свежих данных при каждом входе в бота
lastFetchTime = 0;
errorCount = 0;

export const useSubscriptionLimitsStore = create<SubscriptionLimitsState>()((set, get) => ({
  data: null,
  isLoading: false,
  error: null,

  // Оптимистичное обновление использования (сразу списываем)
  incrementUsageOptimistic: (increments) => {
    const currentData = get().data;
    if (!currentData) return;

    const newUsage = {
      ...currentData.usage,
      messages: currentData.usage.messages + (increments.messages || 0),
      webSearches: currentData.usage.webSearches + (increments.webSearches || 0),
      fileRequests: currentData.usage.fileRequests + (increments.fileRequests || 0),
      imagesGenerated: currentData.usage.imagesGenerated + (increments.images || 0),
    };

    set({
      data: {
        ...currentData,
        usage: newUsage
      }
    });
  },

  // Откат оптимистичного обновления (при ошибке)
  rollbackUsageOptimistic: (increments) => {
    const currentData = get().data;
    if (!currentData) return;

    const newUsage = {
      ...currentData.usage,
      messages: Math.max(0, currentData.usage.messages - (increments.messages || 0)),
      webSearches: Math.max(0, currentData.usage.webSearches - (increments.webSearches || 0)),
      fileRequests: Math.max(0, currentData.usage.fileRequests - (increments.fileRequests || 0)),
      imagesGenerated: Math.max(0, currentData.usage.imagesGenerated - (increments.images || 0)),
    };

    set({
      data: {
        ...currentData,
        usage: newUsage
      }
    });
  },

  fetchLimits: async (forceRefresh = false) => {
    // 1. КРИТИЧНО: Проверяем, не выполняется ли уже запрос
    // НО: если forceRefresh = true (например, при смене режима команды), разрешаем запрос
    // Это важно потому что при переключении team mode запрос с teamId должен выполниться
    if (get().isLoading && !forceRefresh) {
      return;
    }

    // 2. Проверяем кэш
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTime;
    
    // Рассчитываем задержку при ошибках (exponential backoff)
    const errorRetryDelay = Math.min(
      ERROR_RETRY_BASE * Math.pow(2, errorCount),
      MAX_ERROR_RETRY_DELAY
    );
    
    // 3. Если есть данные и кэш свежий - не делаем запрос
    if (!forceRefresh && timeSinceLastFetch < CACHE_DURATION && get().data) {
      return;
    }

    // 4. Если была ошибка недавно - применяем exponential backoff
    if (!forceRefresh && errorCount > 0 && timeSinceLastFetch < errorRetryDelay) {
      return;
    }
    set({ isLoading: true, error: null });

    try {
      if (!hasAuthData()) {
        throw new Error('Not authenticated');
      }

      // Проверяем, находимся ли в режиме команды
      const teamState = useTeamStore.getState();
      const teamId = teamState.isTeamMode ? teamState.activeTeam?.id : undefined;

      const responseData = await apiClient.user.getSubscriptionLimits(teamId);

      if (!responseData.success) {
        throw new Error(responseData.error || 'Failed to fetch limits');
      }

      // Успешный запрос - сбрасываем счётчик ошибок
      errorCount = 0;
      lastFetchTime = now;

      // Smart Merge: используем Math.max для imagesGenerated чтобы не откатывать оптимистичное обновление
      const currentData = get().data;
      const mergedUsage = {
        ...responseData.usage,
        imagesGenerated: Math.max(
          currentData?.usage?.imagesGenerated || 0,
          responseData.usage.imagesGenerated || 0
        )
      };

      set({ 
        data: {
          ...responseData,
          usage: mergedUsage,
          // Сохраняем маппинги моделей с бэкенда (единый источник истины)
          modelMappings: responseData.modelMappings || currentData?.modelMappings
        },
        isLoading: false,
        error: null
      });
    } catch (err: any) {
      // КРИТИЧНО: Обновляем lastFetchTime даже при ошибке
      // Это предотвращает немедленные повторные запросы
      lastFetchTime = now;
      errorCount = Math.min(errorCount + 1, 5); // Ограничиваем до 5 для разумного backoff
      
      set({
        isLoading: false,
        error: err.response?.data?.error || err.message || 'Failed to fetch limits'
      });
    }
  }
}));

// Helper hooks для проверок (используют store)
export const useCanSendMessage = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  if (!data) return { allowed: true };
  
  const limit = data.limits.chat.messagesPerDay;
  // undefined, null, NaN или -1 = безлимит (платные тарифы работают по токенам)
  if (limit === undefined || limit === null || limit === -1 || Number.isNaN(limit)) return { allowed: true };
  
  if (data.usage.messages >= limit) {
    return { 
      allowed: false, 
      reason: `Daily limit reached (${limit} messages)` 
    };
  }
  
  return { allowed: true };
};

export const useCanUseWebSearch = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  if (!data) return { allowed: true };
  
  const limit = data.limits.chat.webSearchPerDay;
  // undefined, null, NaN или -1 = безлимит (платные тарифы работают по токенам)
  if (limit === undefined || limit === null || limit === -1 || Number.isNaN(limit)) return { allowed: true };
  
  if (data.usage.webSearches >= limit) {
    return { 
      allowed: false, 
      reason: `Web search limit reached (${limit}/${limit})` 
    };
  }
  
  return { 
    allowed: true,
    reason: `${data.usage.webSearches}/${limit} used`
  };
};

export const useCanUseFiles = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  if (!data) return { allowed: true };
  
  const limit = data.limits.chat.filesPerDay;
  // undefined, null, NaN или -1 = безлимит (платные тарифы работают по токенам)
  if (limit === undefined || limit === null || limit === -1 || Number.isNaN(limit)) return { allowed: true };
  
  if (data.usage.fileRequests >= limit) {
    return { 
      allowed: false, 
      reason: `File limit reached (${limit}/${limit})` 
    };
  }
  
  return { 
    allowed: true,
    reason: `${data.usage.fileRequests}/${limit} used`
  };
};

export const useCanUseTranscription = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  if (!data) return { allowed: true };
  
  if (!data.limits.chat.transcription) {
    return {
      allowed: false,
      reason: 'Транскрибация недоступна в вашем тарифе'
    };
  }
  
  return { allowed: true };
};

export const useIsModelAllowed = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  return (provider: string, model: string): boolean => {
    if (!data) return true;
    const providerModels = data.limits.chat.allowedModels[provider as keyof typeof data.limits.chat.allowedModels];
    return providerModels ? providerModels.includes(model) : false;
  };
};

export const useIsThinkingModeAllowed = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  return (level: string): boolean => {
    if (!data) return true;
    const thinkingMode = data.limits.chat.thinkingMode;
    if (thinkingMode === false) return false;
    if (thinkingMode === true) return true;
    if (Array.isArray(thinkingMode)) return thinkingMode.includes(level);
    return false;
  };
};

export const useCanGenerateImages = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  return (count: number = 1): { allowed: boolean; reason?: string } => {
    if (!data) return { allowed: true };
    
    const dailyLimit = data.limits.images.imagesPerDay;
    const maxPerGeneration = data.limits.images.maxImagesPerGeneration;
    
    if (count > maxPerGeneration) {
      return {
        allowed: false,
        reason: `Maximum ${maxPerGeneration} images per generation`
      };
    }
    
    // undefined, null, NaN или -1 = безлимит (платные тарифы работают по токенам)
    if (dailyLimit !== undefined && dailyLimit !== null && !Number.isNaN(dailyLimit) && dailyLimit !== -1 && data.usage.imagesGenerated >= dailyLimit) {
      return {
        allowed: false,
        reason: `Daily limit reached (${dailyLimit} images)`
      };
    }
    
    return { allowed: true };
  };
};

export const useCanGenerateVideo = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  return (): { allowed: boolean; reason?: string } => {
    if (!data) return { allowed: true };
    
    if (!data.limits.video.enabled) {
      return {
        allowed: false,
        reason: 'Video generation not available in your plan'
      };
    }
    
    return { allowed: true };
  };
};

export const useIsImageProviderAllowed = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  return (provider: string): boolean => {
    if (!data) return true;
    const providerConfig = data.limits.images.allowedProviders[provider];
    return providerConfig?.enabled || false;
  };
};

export const useIsVideoProviderAllowed = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  return (provider: string): boolean => {
    if (!data) return true;
    const providerConfig = data.limits.video.allowedProviders[provider];
    return providerConfig?.enabled || false;
  };
};

export const useGetImageProviderConfig = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  return (provider: string) => {
    if (!data) return null;
    return data.limits.images.allowedProviders[provider] || null;
  };
};

export const useGetVideoProviderConfig = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  return (provider: string) => {
    if (!data) return null;
    return data.limits.video.allowedProviders[provider] || null;
  };
};

export const useGetModelUsage = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  
  return (model: string): { used: number; limit: number } => {
    if (!data) return { used: 0, limit: -1 };
    const used = data.usage.modelUsage[model] || 0;
    const limit = data.limits.chat.dailyModelLimits[model] || -1;
    return { used, limit };
  };
};

export const useCanUseModel = () => {
  const data = useSubscriptionLimitsStore(state => state.data);
  const getModelUsage = useGetModelUsage();
  
  return (model: string): { allowed: boolean; reason?: string } => {
    if (!data) return { allowed: true };
    
    const { used, limit } = getModelUsage(model);
    
    if (limit === -1) return { allowed: true };
    
    if (used >= limit) {
      return {
        allowed: false,
        reason: `Daily limit for ${model} reached (${limit}/${limit})`
      };
    }
    
    return {
      allowed: true,
      reason: `${used}/${limit} used`
    };
  };
};

